import { Box, CircularProgress, Grid, Paper, Stack, Typography } from "@mui/material";
import ConnectedAccount from "../../../models/ConnectedAccount";
import { FC, useCallback, useEffect, useState } from "react";
import ExportTechnicalButton from "../common/ExportTechnicalControls";
import { useListSecurityControlsQuery } from "../../../hooks/useListSecurityControlsQuery";
import LoadingButton from "@mui/lab/LoadingButton";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import useNewrelic from "../../../hooks/useNewrelic";
import { useDownloadSelfAssessment } from "../../../hooks/useDownloadSelfAssessment";

interface FTRGenerateReportsProps {
	technicalReviewArn: string;
	applicationName: string;
	connectedAccounts: Array<ConnectedAccount>;
	setUnlockSteps: React.Dispatch<
		React.SetStateAction<{
			step2: boolean;
			step3: boolean;
			step4: boolean;
			step5: boolean;
		}>
	>;
	changeTab: (
    index: number,
  ) => void;
}

/**
 * Page to Generate Reports
 * @param technicalReviewArn arn of user
 * @param connectedAccounts state of connectedAccounts information
 * @param setUnlockSteps which step to unlock and lock in the tabs
 * @param changeTab tab to switch to after this page is completed
 * @returns 
 */
const FTRGenerateReports: FC<FTRGenerateReportsProps> = ({
	technicalReviewArn,
	applicationName,
	connectedAccounts,
	setUnlockSteps,
	changeTab
}: FTRGenerateReportsProps) => {
	const { noticeError } = useNewrelic();
	const [cisControlsData, setCISControlsData] = useState([]);
  const [activeSpinner, setActiveSpinner] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const { downloadSelfAssessment, loading: downloadSelfAssessmentLoading } = useDownloadSelfAssessment();
	const { controls, loading } = useListSecurityControlsQuery({
		arn: technicalReviewArn,
		connectedAccounts,
	});

	useEffect(() => {
		if (loading) return;
		const filteredControls = controls.filter((control: any) => 
			Array.isArray(control.customStandard) &&
			control.customStandard.includes(
				"CIS Benchmark for CIS Amazon Web Services Foundations Benchmark, v1.4.0, Level 1 and 2"
			)
		);
		setCISControlsData(filteredControls);
	}, [loading, controls]);

	const handleDownload = async () => {
    setActiveSpinner(true);
    setDisabled(true);
    try {
      await downloadSelfAssessment(technicalReviewArn, applicationName);
    } catch (error) {
      noticeError(error, "Error downloading reports");
    } finally {
      setActiveSpinner(false);
      setDisabled(false);
    }
  }

	//Handle that unlocks step 5
	const handleUnlockSteps = useCallback(() => {
		setUnlockSteps((prevState) => ({
			...prevState,
			step5: false, // Lock step 5
		}));
	}, []);

	const handleSubmit = useCallback(() => {
		handleUnlockSteps();
		changeTab(5);
	}, [handleUnlockSteps, changeTab]);

	return (
		<Grid
			container
			sx={{ p: 2 }}
			spacing={2}
			alignItems="stretch"
			flexDirection={"column"}
			component={Paper}
			maxWidth="lg"
		>
			<Grid container alignItems="center">
				<Grid item xs md={12} sx={{ pt: 2 }}>
					<Typography variant="h4" color="primary.main" gutterBottom>
						Generate FTR Reports
					</Typography>
				</Grid>	
				<Grid item xs md={4} sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ fontWeight: "bold", ml: 3 }}>
							Generate CIS v1.4.0 Controls Report
						</Typography>
				</Grid>
				<Grid item xs md={8} sx={{ mb: 2 }}>
					<Stack direction="row">
						<ExportTechnicalButton
							type={"CSV"}
							disabled={loading || cisControlsData.length === 0}
							table={cisControlsData}
							filter={{
								"complianceStandard":"CIS Benchmark for CIS Amazon Web Services Foundations Benchmark, v1.4.0, Level 1 and 2",
								"compliant": "",
								"destructive": "",
							}}
							sx={{
								m: 0,
								ml: 1,
								mb: 1,
								width: "50%",
								fontWeight: 600,
							}}
						/>
						<ExportTechnicalButton
							type={"PDF"}
							disabled={loading || cisControlsData.length === 0}
							table={cisControlsData}
							filter={{
								"complianceStandard":"CIS Benchmark for CIS Amazon Web Services Foundations Benchmark, v1.4.0, Level 1 and 2",
								"compliant": "",
								"destructive": "",
							}}
							sx={{
								m: 0,
								ml: 1,
								mb: 1,
								width: "50%",
								flexGrow: 0,
								fontWeight: 600,
							}}
						/>
					</Stack>
				</Grid>
				<Grid item xs md={4} sx={{ mb: 2 }}>
					<Typography variant="body2" sx={{ fontWeight: "bold", ml: 3 }}>
						FTR Self-Assessment Questionnaire Checklist
					</Typography>
				</Grid>
				<Grid item xs md={8} sx={{ mb: 2 }}>
					<LoadingButton
						onClick={() => handleDownload()} 
						id="ftr-download-self-assessment-btn" 
						type="submit" 
						variant="outlined"
						disabled={disabled}
						loading={downloadSelfAssessmentLoading || activeSpinner}
						endIcon={<CloudDownloadOutlinedIcon />}
						sx={{
							m: 0,
							ml: 1,
							mb: 1,
							width: "50%",
							flexGrow: 0,
							fontWeight: 600,
						}}
						loadingIndicator={
							<CircularProgress color={"secondary"} size={22} />
						}
						>
						Update & Download
					</LoadingButton>
				</Grid>
			</Grid>
			<Grid item xs md={12}>
				<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
					<LoadingButton
						variant="contained"
						color="secondary"
						type="submit"
						disabled={loading || downloadSelfAssessmentLoading || cisControlsData.length === 0}
						onClick={handleSubmit}
						sx={{
							width: 157,
							height: 40,
							bgcolor: "secondary.main",
							"&:hover": {
								bgcolor: "secondary.main",
								color: "secondary.contrastText",
							},
						}}
					>
						Next
					</LoadingButton>
				</Box>
			</Grid>
		</Grid>
	)
};

export default FTRGenerateReports;